import { Badge, Icon, Tooltip } from "@deligoo/ui"
import { CSSProperties, memo } from "react"
import { useTranslation } from "react-i18next"
import { areEqual } from "react-window"

import { useAppSelector } from "@/App"
import { Clipboard } from "@/components/Clipboard"
import { ContextMenu, openContextMenu } from "@/components/ContextMenu"
import { Table } from "@/components/Table"
import { useDriversContextMenuTrigger } from "@/modules/DriverContextMenu"
import { selectDriverById } from "@/store/drivers"
import { Driver } from "@/types/Driver"
import { DriverUtils, formatPhoneNumber, getIconFor } from "@/utils"

import cls from "./DriversListView.module.scss"

type DriversListRowProps = {
  data: Array<Driver["id"]>
  index: number
  style: CSSProperties
}

export const DriversListRow = memo(({ data, index, style }: DriversListRowProps) => {
  const driverId = data[index]

  return <DriversListItem key={driverId} driverId={driverId} style={style} index={index} />
}, areEqual)

type DriversListItemProps = {
  driverId: Driver["id"]
  index: number
  style: CSSProperties
}

const DriversListItem = ({ driverId, index, style }: DriversListItemProps) => {
  const contextMenuRef = useDriversContextMenuTrigger({ driverId })

  const { t } = useTranslation()

  const driver = useAppSelector(selectDriverById(driverId))
  if (!driver) return null

  const system = DriverUtils.getSystem(driver.drivers_app_version)
  const appVersion = DriverUtils.getAppVersion(driver.drivers_app_version)

  return (
    <Table.Tr style={style} ref={contextMenuRef}>
      <Table.Td>{index + 1}</Table.Td>

      <Table.Td>{driver.name}</Table.Td>

      <Table.Td>{driver.team.name}</Table.Td>

      <Table.Td>{driver.phone ? <Clipboard>{formatPhoneNumber(driver.phone)}</Clipboard> : "–"}</Table.Td>

      <Table.Td>
        <Clipboard className="truncate">{driver.email}</Clipboard>
      </Table.Td>

      <Table.Td className="text-center">
        {appVersion ? (
          <Tooltip content={appVersion} theme="light">
            <span>
              <Icon
                size="large"
                name={system === "android" ? "android" : "apple"}
                color={system === "android" ? "success" : "dark"}
              />
            </span>
          </Tooltip>
        ) : (
          <span>-</span>
        )}
      </Table.Td>

      <Table.Td className="capitalize">{driver.payment_collection_system || "-"}</Table.Td>

      <Table.Td>
        {(["cash", "card"] as const).map(
          (paymentForm) =>
            !driver.payment_forms.includes(paymentForm) && (
              <Tooltip content={t(`drivers:${paymentForm}`)}>
                <span className={cls.disabledPaymentForm}>
                  <Icon key={paymentForm} name={getIconFor(paymentForm)} color="alert" size="small" />
                </span>
              </Tooltip>
            )
        )}
      </Table.Td>

      <Table.Td>{DriverUtils.getCurrentWorkingHours(driver) || "-"}</Table.Td>

      <Table.Td>
        <Blocks driver={driver} />
      </Table.Td>

      <Table.Td>{driver.unsettled_orders_count}</Table.Td>

      <Table.Td>
        <Icon name={getIconFor(driver.transport_type)} color="primary" size="large" />
        <span className="ml-2">{driver.transport?.includes("own") ? "Własny" : "Firmowy"}</span>
      </Table.Td>

      <Table.Td>
        <Badge size="small" color={driver.is_available ? "success" : "alert"}>
          {driver.is_available ? "Online" : "Offline"}
        </Badge>
      </Table.Td>

      <Table.Td className="text-right">
        <ContextMenu.Button onClick={openContextMenu} />
      </Table.Td>
    </Table.Tr>
  )
}

const Blocks = ({ driver }: { driver: Driver }) => {
  if (DriverUtils.isAfterShift(driver)) {
    const { shiftEnd } = DriverUtils.getCurrentShiftHours(driver) || {}
    return (
      <Tooltip content={`Blokada${shiftEnd && ` od ${shiftEnd}`}`}>
        <span>Blokada</span>
      </Tooltip>
    )
  }

  if (driver.is_on_break) {
    const nextBreak = DriverUtils.getNextTodayBreak(driver)
    const breakDuration = nextBreak ? DriverUtils.getBreakDuration(nextBreak) : null

    return (
      <Tooltip content={`Przerwa${nextBreak && ` od ${nextBreak.start_time} (${breakDuration} min)`}`}>
        <span>Przerwa</span>
      </Tooltip>
    )
  }

  return <span>-</span>
}
